/**
 * Welche Kommentararten das Blatt anbietet.
 *
 * **Auftrag des Nutzers vom 2026-09-18:** jede Art einzeln abschaltbar, in der App und in den
 * iPad-Einstellungen; vorgabeweise sind alle da. Nicht jede Station braucht jede Art — an einer
 * reinen Gesprächsstation ist ein Videokommentar bloss ein Knopf, der zum Fehlgriff einlädt.
 *
 * Reine Entscheidung: kein React, kein Store, kein Gerätezugriff. Wo die Schalter herkommen,
 * steht im Katalog (`*_comments`), wo sie wirken in `features/comment/CommentSheet.tsx`,
 * `features/bogen/TaskCard.tsx` und `features/bogen/Bogen.tsx`.
 *
 * ⚠️ **„Abgeschaltet heisst weg", auch mit Inhalt** (Entscheidung des Nutzers, 2026-09-18, auf
 * ausdrückliche Rückfrage). Liegt auf dem Gerät schon eine Aufnahme einer abgeschalteten Art,
 * verschwindet sie mit ihrem Reiter aus der Oberfläche — sie ist dann **nicht mehr sicht- und
 * löschbar**, geht aber weiter an den Server, weil der Takt die Protokollzeilen unabhängig von
 * der Oberfläche schickt. Praktisch trifft es selten: die Schalter setzt die Aufsicht vor der
 * Prüfung, nicht mitten hinein. Wer das ändern will, ändert es hier und an den Zählern
 * (`domain/scoring.ts::commentCount`) — nicht an einer der drei Anzeigestellen.
 */

export type KommentarArt = 'text' | 'handwriting' | 'audio' | 'photo' | 'video';

export type KommentarArten = Record<KommentarArt, boolean>;

/**
 * Die Reihenfolge der Reiter im Kommentarblatt.
 *
 * ⚠️ **Sie ist die Wahrheit, nicht die Reihenfolge der Schalter.** Ein Prüfer, dem die Reiter
 * je nach Einstellung anders sortiert erscheinen, sucht jedes Mal neu.
 */
export const ALLE_ARTEN: readonly KommentarArt[] = [
  'text',
  'handwriting',
  'audio',
  'photo',
  'video',
];

/** Die erlaubten Arten, in der Reihenfolge des Blattes. */
export function erlaubteArten(arten: KommentarArten): KommentarArt[] {
  return ALLE_ARTEN.filter((art) => arten[art]);
}

/**
 * Ist keine einzige Art erlaubt?
 *
 * Dann gibt es **keinen** Kommentarweg mehr: kein Knopf an der Aufgabe, kein Stationskasten.
 * Ein Blatt ohne Reiter wäre ein Fehler, kein Zustand.
 */
export function keineErlaubt(arten: KommentarArten): boolean {
  return erlaubteArten(arten).length === 0;
}
